import React from "react";
import { NavigationType } from "../../models/enums/NavigationType";
import { ValidationResultModel } from "../../models/ValidationResultModel";
import { ThemeModel } from "../../models/ThemeModel";
import { getSettingString, getSettingEnum } from "../../models/utilities/settingUtilities";
import { Signal } from "../../signals/index";
import { Voxco } from "../../public";
import { ValidationEvent } from "../../public/events";
import { ValidationOptionsModel } from "../../models/ValidationOptionsModel";
import { QuitButtonAction } from "../../models/enums/QuitButtonAction";
import { QuestionnaireModel } from "../../models/QuestionnaireModel";
import { ValidationBuilder } from "../../models/builders/ValidationBuilder";
import PageRenderer from "../renderers/v2/PageRenderer";
import { ThunkDispatchProp } from "../../models/actions/ThunkDispatchProp";
import {
    validationClear,
    validationSetResult,
    validationAnnounceBump,
    navigationPreviousPage,
    navigationBranchTo,
    navigationNextPage,
} from "../../redux/actions";
import {
    validate,
    getVisibleQuestions,
    validateAndNavigateToNextPage,
} from "../../redux/thunks/validation";
import { isValid } from "../../models/utilities/validationUtilities";
import { QuestionModel } from "../../models/QuestionModel";
import { exitSurvey } from "../../redux/thunks/exitSurvey";
import {
    handleProbingBeforeNavigation,
    DISABLE_PROBING,
} from "../../redux/thunks/probingOrchestrator";
import ProbingLoadingIndicator from "../shared/ProbingLoadingIndicator";
import { addVoxcoAgentAuditLog } from "../../utils/voxcoAgentHelper";

interface Props extends ThunkDispatchProp {
    isValid: boolean;
    questions: QuestionModel[];
    message: string | Error;
    language: string;
    validation: ValidationResultModel;
    questionnaire: QuestionnaireModel;
    matrixIndex: number;
    isNavigationDisabled: boolean;
    progress: number;
    title: string;
    theme: ThemeModel;
    useClientValidation: boolean;
    isDesktopPreviewView?: boolean;
    parent?: string;
}

// Set this callback to the current page's validation function.
let validationCallback: (ev?: ValidationEvent) => ValidationResultModel;

// Set up page validation event.
Voxco.on("validatePage", (ev) => (validationCallback ? validationCallback(ev) : ev));

export class Page extends React.PureComponent<Props> {
    validateSignal = new Signal<ValidationOptionsModel, ValidationResultModel>();

    logAuditForNavigation = (type: NavigationType, parent?: string) => {
        const auditEventByNavigationType: Record<NavigationType, string> = {
            [NavigationType.Next]: "NextButtonClicked",
            [NavigationType.Submit]: "SubmitButtonClicked",
            [NavigationType.Back]: "PreviousButtonClicked",
            [NavigationType.Quit]: "QuitButtonClicked",
        };
        const event = auditEventByNavigationType[type];
        if (event !== undefined) {
            addVoxcoAgentAuditLog(event, parent);
        }
    };

    hasIncompleteAnswer = (): boolean => {
        const { validation } = this.props;
        if (!validation || !validation.errorsByVariableName) {
            return false;
        }
        return Object.values(validation.errorsByVariableName).some((errors) =>
            errors.some((error) => error.key === "IncompleteAnswer"),
        );
    };

    componentDidMount() {
        validationCallback = this.onValidate;
        this.resizeImages();
        this.setViewportContent();
        window.onresize = () => this.resizeImages();
    }

    onValidate = (ev: ValidationEvent): ValidationResultModel => {
        // Prevent default behavior.
        if (ev.defaultPrevented) {
            return new ValidationBuilder().build();
        }
        const results = this.validateSignal.dispatch(ev.options);
        const result = results.reduce(
            (a, b) => ({
                isValid: a.isValid && isValid(b),
                errorsByVariableName: { ...a.errorsByVariableName, ...b.errorsByVariableName },
            }),
            { isValid: true, errorsByVariableName: {} },
        );
        // Set the current state if the validation is not silent.
        if (!ev.options || !ev.options.silent) {
            const { dispatch } = this.props;
            dispatch(validationClear(false, true));
            dispatch(validationSetResult(result));
        }
        return result;
    };

    onNavigation = (type: NavigationType) => {
        const { questionnaire, isNavigationDisabled, useClientValidation, dispatch, parent } =
            this.props;

        const quit =
            type === NavigationType.Quit
                ? {
                      action: getSettingEnum(questionnaire, QuitButtonAction, "QUIT_BUTTON_ACTION"),
                      destination: getSettingString(
                          questionnaire,
                          "QUIT_BUTTON_BRANCH_TO_QUESTION",
                      ),
                  }
                : undefined;
        // Focus reset moved to PageContainer.componentDidUpdate (fires only when the
        // page actually navigates). Resetting focus to #root here ran on every Next
        // click — including validation failures that don't navigate — and caused NVDA
        // to read the page from #root after each failure.

        this.logAuditForNavigation(type, parent);

        switch (type) {
            case NavigationType.Next:
            case NavigationType.Submit:
                if (isNavigationDisabled) {
                    break;
                }

                // If there are incomplete answers, we want to trigger the validation summary announcement even if client validation is disabled, since the server will reject the navigation and the user needs to be informed about the incomplete answers. (DEV-97358)
                if (this.hasIncompleteAnswer()) {
                    dispatch(validationAnnounceBump());
                }

                if (!useClientValidation) {
                    Voxco.validatePage({ silent: true });

                    // For tests, skip the probing check
                    if (DISABLE_PROBING) {
                        dispatch(navigationNextPage());
                        break;
                    }

                    // Check if probing is needed before navigation
                    dispatch(handleProbingBeforeNavigation()).then((shouldContinue) => {
                        if (shouldContinue) {
                            dispatch(navigationNextPage());
                        }
                    });
                    break;
                }
                if (
                    process.env.DISABLE_CLIENT_VALIDATION !== "true" &&
                    !Voxco.trigger("validatePage").isValid
                ) {
                    const questions = dispatch(getVisibleQuestions());
                    dispatch(validate(questions));
                    // Re-announce the error in screen readers even when the validation
                    // result is identical to the previous attempt. role="alert" only
                    // fires on mount/text-change; bumping this re-keys (remounts) the
                    // alert nodes so a repeated failed submit speaks again. Scoped to
                    // Next/Submit so inline edits don't re-announce. (DEV-97358)
                    dispatch(validationAnnounceBump());
                    break;
                }

                // For tests, skip the probing check
                if (DISABLE_PROBING) {
                    dispatch(validateAndNavigateToNextPage());
                    break;
                }

                // Check if probing is needed before proceeding with validation and navigation
                dispatch(handleProbingBeforeNavigation()).then((shouldContinue) => {
                    if (shouldContinue) {
                        dispatch(validateAndNavigateToNextPage());
                    }
                });
                break;
            case NavigationType.Back:
                if (isNavigationDisabled) {
                    break;
                }
                dispatch(navigationPreviousPage());
                break;
            case NavigationType.Quit: {
                if (quit && quit.destination && quit.action === QuitButtonAction.BranchTo) {
                    dispatch(
                        navigationBranchTo({ destination: quit.destination, isQuitButton: true }),
                    );
                } else {
                    dispatch(exitSurvey());
                }
                break;
            }
            default:
                break;
        }
    };

    setViewportContent() {
        if (window.navigator.appVersion.includes("iPhone")) {
            let viewport = document.querySelector<HTMLMetaElement>("meta[name='viewport']");
            if (!viewport) {
                // viewport meta element is removed by some webviews
                // add it programmatically
                viewport = document.createElement("meta");
                viewport.name = "viewport";
                viewport.content = "width=device-width, initial-scale=1";
                document.head.appendChild(viewport);
            }
            viewport.content += ", maximum-scale=1";
        }
    }

    resizeImages() {
        ["pageHeader", "pageFooter"].forEach((c) => {
            Array.from(document.getElementsByClassName(c)).forEach((element: HTMLElement) => {
                Array.from(element.getElementsByTagName("img")).forEach((imgElement) => {
                    const maxHeightPercent = 25;
                    const currentHeightPercent = (imgElement.height / window.innerHeight) * 100;
                    if (currentHeightPercent !== maxHeightPercent) {
                        imgElement.style.maxHeight = `${
                            (window.innerHeight * maxHeightPercent) / 100
                        }px`;
                        imgElement.style.maxWidth = "100%";
                        imgElement.style.height = imgElement.style.height || "auto";
                        imgElement.style.width = imgElement.style.width || "auto";
                    }
                });
            });
        });
    }

    render() {
        return (
            <>
                <PageRenderer
                    onNavigation={this.onNavigation}
                    validateSignal={this.validateSignal}
                    {...this.props}
                />
                <ProbingLoadingIndicator />
            </>
        );
    }
}
